// modules/facturacion/components/DetalleFacturaModal.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Alert, Grid, TextField, Snackbar,
  MenuItem, IconButton, Tooltip, Checkbox, Paper,
  Tabs, Tab, TablePagination, Collapse, useMediaQuery
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon   from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon  from "@mui/icons-material/Print";
import EmailIcon  from "@mui/icons-material/Email";
import PaymentIcon from "@mui/icons-material/Payment";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendIcon from "@mui/icons-material/Send";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import ShareIcon from "@mui/icons-material/Share";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import axios from "../../../config/axiosConfig";
import * as svc from "../services/facturacionService";
import { useAuth } from "../../../context/AuthContext";
import facturaPagadaImg from "../../../assets/factura-pagada.png";
import EditarFacturaModal from "./EditarFacturaModal";
import PagosRelacionadosDialog from "./PagosRelacionadosDialog";
import MovimientoRelacionesDialog from "../../egresos/components/MovimientoRelacionesDialog";

const infoFieldSx = {
  minWidth: 0,
  px: 0.9,
  py: 0.65,
  border: "1px solid",
  borderColor: "divider",
  borderRadius: 1,
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.action.hover
      : "#f8fafc",
};
const softPanelSx = {
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.background.paper
      : "#fafafa",
};
const warningPanelSx = {
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.action.hover
      : "#fff7ed",
  borderColor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.warning.dark
      : "#fed7aa",
};
const warningHeadSx = {
  bgcolor: (theme) =>
    theme.palette.mode === "dark"
      ? theme.palette.action.selected
      : "#ffedd5",
  "& th": {
    color: (theme) =>
      theme.palette.mode === "dark"
        ? theme.palette.warning.light
        : "#7c2d12",
  },
};
const formatJson = (value) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value || "");
  }
};
const TIPOS_DOCUMENTO = [
  { codigo: 1, nombre: "CI" },
  { codigo: 2, nombre: "CEX" },
  { codigo: 3, nombre: "PAS" },
  { codigo: 4, nombre: "OD" },
  { codigo: 5, nombre: "NIT" },
];
const TIPOS_INGRESO = [
  { value: "1", label: "Servicios" },
  { value: "2", label: "Franquicia" },
  { value: "3", label: "Intercambio Serv." },
  { value: "4", label: "Castigo Deuda" },
];
const getTipoIngresoLabel = (value) =>
  TIPOS_INGRESO.find((t) => String(t.value) === String(value))?.label || value || "-";

// Estilos globales ultra-compactos para que los formularios no ocupen espacio innecesario
const compactFieldSx = {
  "& .MuiInputBase-root": { minHeight: 28, fontSize: "0.76rem", borderRadius: "4px" },
  "& .MuiInputBase-input": { py: 0.4 },
  "& .MuiInputLabel-root": { fontSize: "0.75rem", transform: "translate(14px, 6px) scale(1)" },
  "& .MuiInputLabel-shrink": { transform: "translate(14px, -6px) scale(0.75)" },
  "& .MuiSelect-select": { py: "4px" },
};

const normalizeMotivoAnulacion = (m = {}) => ({
  codigo: String(m.codigo ?? m.codigoClasificador ?? m.id ?? ""),
  descripcion: m.descripcion ?? m.nombre ?? m.texto ?? m.label ?? "",
});
const stripCuotaDetalle = (detalle = "") =>
  String(detalle).replace(/\s*\.?\s*Cuota No:\s*[^.]+$/i, "").trim();

const pad2 = (value) => String(value).padStart(2, "0");
const formatLocalYmd = (date) =>
  `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
const todayYmd = () => formatLocalYmd(new Date());
const tomorrowYmd = () => {
  const next = new Date();
  next.setDate(next.getDate() + 1);
  return formatLocalYmd(next);
};
const money = (value) => Number(value || 0).toFixed(2);
// Columnas DATE (fecha_fac): llegan como "2026-10-08T04:00:00.000Z"; se toma solo la parte de la fecha para no correr el día
const fechaSola = (value) => {
  const m = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
// Columnas DATETIME (fecha_emi): son un instante real, se muestran en hora local
const fechaConHora = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return fechaSola(value);
  const dos = (n) => String(n).padStart(2, "0");
  return `${dos(d.getDate())}/${dos(d.getMonth() + 1)}/${d.getFullYear()} ${dos(d.getHours())}:${dos(d.getMinutes())}`;
};
const unwrapData = (res) => res?.data?.data ?? res?.data;
const parseDecimal = (value, fallback = 0) => {
  if (value === "" || value === null || value === undefined) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
};
const normalizeDetalleGrupos = (grupos = [], detalles = []) => {
  if (!Array.isArray(grupos)) return [];
  const detallesList = Array.isArray(detalles) ? detalles : [];

  return grupos.map((grupo) => {
    const lineas = Array.isArray(grupo.lineas)
      ? grupo.lineas
      : detallesList.filter((d) =>
        Number(d.id_detalle_grupo || 0) === Number(grupo.id || 0) ||
        (grupo.group_key && d.group_key === grupo.group_key)
      );
    const descuentoLineas = lineas.reduce((acc, linea) => acc + Math.abs(parseDecimal(linea.descuento_interno)), 0);
    const descuentoGrupo = Math.abs(parseDecimal(grupo.descuento_interno));

    return {
      ...grupo,
      lineas,
      descuento_interno: descuentoGrupo > 0 ? descuentoGrupo : descuentoLineas,
    };
  });
};

const PagoFacturaDialog = ({ open, onClose, cod, factura, onSuccess, showToast }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [pagos, setPagos] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [extractos, setExtractos] = useState([]);
  const [extractosTotal, setExtractosTotal] = useState(0);
  const [extractosPage, setExtractosPage] = useState(0);
  const [extractosPageSize, setExtractosPageSize] = useState(10);
  const [extractoId, setExtractoId] = useState("");
  const [buscarExtracto, setBuscarExtracto] = useState("");
  const [form, setForm] = useState({});
  const [qrForm, setQrForm] = useState({});
  const [qrResumen, setQrResumen] = useState({ actual: null, historial: [], eventos: [], stats: { total: 0 } });
  const [mostrarQrGenerado, setMostrarQrGenerado] = useState(false);
  const [qrPreview, setQrPreview] = useState(null);
  const [qrResumenInicializado, setQrResumenInicializado] = useState(false);
  const initKeyRef = useRef("");
  const facturaPagada = factura?.pagada === "si";
  const puedeEliminarPago = ["admin", "conta"].includes(String(user?.role || "").toLowerCase());

  const buildDefaultForm = useCallback(() => {
    const cambio = Number(factura?.cambio) === 1
      ? Number(factura?.cambio_ref || 1)
      : Number(factura?.cambio || 1);
    return {
      pago_moneda: "bolivianos",
      forma_pago: "Efectivo",
      tipo_ingreso: "1",
      moneda: cambio || 1,
      monto: money(factura?.saldo ?? factura?.monto_total ?? 0),
      fecha_pago: todayYmd(),
      fecha_deposito: todayYmd(),
      numcheque: "",
      numrecibo: "",
      banco_pago: "",
      banco_deposito: "",
      cod_banco: "",
      razonpago: `${factura?.mes || ""} de ${factura?.year || ""}`.trim(),
      obs: "",
      cobrador: user?.username || "",
      sucursal: user?.sucursal || factura?.sucursal || "",
    };
  }, [factura, user]);

  const buildDefaultQrForm = useCallback(() => ({
    amount: money(factura?.saldo ?? factura?.monto_total ?? 0),
    expirationDate: tomorrowYmd(),
    gloss: `Factura TRS ${factura?.num_fac || factura?.coddos || factura?.cod || cod || ""}`.trim(),
    additionalData: `FACTURA:${factura?.cod || cod || ""}|SERVICIO:${factura?.id_servicio_cliente || ""}|CLIENTE:${factura?.cod_cliente || ""}`,
    singleUse: true,
  }), [factura, cod]);

  const cargarPagos = useCallback(async () => {
    if (!open || !cod) return;
    setLoading(true);
    try {
      const [resPagos, resBancos] = await Promise.all([
        svc.getPagosFactura(cod),
        axios.get("/api/bancos", { params: { propio: 1, limit: 200, order: "asc" } }),
      ]);
      const pagosData = unwrapData(resPagos);
      setPagos(Array.isArray(pagosData?.pagos) ? pagosData.pagos : []);
      setBancos(Array.isArray(unwrapData(resBancos)) ? unwrapData(resBancos) : []);
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudieron cargar los pagos", "error");
    } finally {
      setLoading(false);
    }
  }, [open, cod, showToast]);

  const cargarExtractos = useCallback(async () => {
    if (!open || facturaPagada) return;
    try {
      const res = await svc.getExtractosDisponibles({
        buscar: buscarExtracto,
        page: extractosPage + 1,
        pageSize: extractosPageSize,
      });
      const data = unwrapData(res);
      setExtractos(Array.isArray(data.items) ? data.items : []);
      setExtractosTotal(Number(data.total || 0));
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudieron cargar los extractos", "error");
    }
  }, [open, facturaPagada, buscarExtracto, extractosPage, extractosPageSize, showToast]);

  const cargarQrResumen = useCallback(async ({ silent = false } = {}) => {
    if (!open || !cod || facturaPagada) return;
    if (!silent) setQrLoading(true);
    try {
      const res = await svc.getFacturaQrResumen(cod);
      const data = unwrapData(res) || {};
      setQrResumen({
        actual: data.actual || null,
        historial: Array.isArray(data.historial) ? data.historial : [],
        eventos: Array.isArray(data.eventos) ? data.eventos : [],
        stats: data.stats || { total: 0 },
      });
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudo cargar el historico QR", "error");
    } finally {
      setQrResumenInicializado(true);
      if (!silent) setQrLoading(false);
    }
  }, [open, cod, facturaPagada, showToast]);

  useEffect(() => {
    if (open && cod) {
      const initKey = `${cod}-${factura?.cod || ""}`;
      if (initKeyRef.current === initKey) return;
      initKeyRef.current = initKey;
      setTab(0);
      setForm(buildDefaultForm());
      setQrForm(buildDefaultQrForm());
      setQrResumen({ actual: null, historial: [], eventos: [], stats: { total: 0 } });
      setMostrarQrGenerado(false);
      setQrPreview(null);
      setQrResumenInicializado(false);
      setExtractoId("");
      setBuscarExtracto("");
      setExtractosPage(0);
      cargarPagos();
      cargarQrResumen({ silent: true });
    } else if (!open) {
      initKeyRef.current = "";
      setQrResumenInicializado(false);
    }
  }, [open, cod, factura?.cod, buildDefaultForm, buildDefaultQrForm, cargarPagos, cargarQrResumen]);

  useEffect(() => {
    const t = setTimeout(() => {
      cargarExtractos();
    }, 250);
    return () => clearTimeout(t);
  }, [cargarExtractos]);

  useEffect(() => {
    if (open && tab === 2 && !facturaPagada && !qrResumenInicializado) {
      cargarQrResumen();
    }
  }, [open, tab, facturaPagada, qrResumenInicializado, cargarQrResumen]);

  const handleForm = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const handleQrForm = (field, value) => setQrForm((p) => ({ ...p, [field]: value }));
  const saldoDisponibleExtracto = (extracto) => {
    const saldoRaw = String(extracto?.saldo ?? "").trim();
    return Number(saldoRaw === "" ? extracto?.credito : extracto?.saldo) || 0;
  };
  const seleccionarExtracto = (extracto) => {
    const id = String(extracto?.id || "");
    setExtractoId(id);
  };
  const extractosDisponibles = extractos.filter((e) => saldoDisponibleExtracto(e) > 0);
  const qrStatusColor = (estado) => {
    switch (String(estado || "").toLowerCase()) {
      case "generado":
      case "no_usado":
        return "success";
      case "pendiente":
        return "info";
      case "expirado":
        return "warning";
      case "pagado":
      case "usado":
        return "primary";
      case "error":
      case "con_error":
      case "cancelado":
        return "error";
      case "reemplazado":
        return "secondary";
      default:
        return "default";
    }
  };

  const buildQrPreview = useCallback((item) => {
    if (!item?.qr_data_url) return null;
    return {
      dataUrl: item.qr_data_url,
      estado: item.estado || "",
      label: item.provider_qr_id || item.id || "",
      amount: item.amount || "",
      expirationDate: item.expiration_date || "",
      gloss: item.gloss || "",
    };
  }, []);

  const buildQrShareText = useCallback((preview = qrPreview) => {
    const partes = [
      `QR factura ${factura?.num_fac || factura?.coddos || cod || ""}`.trim(),
      preview?.amount ? `Monto Bs ${money(preview.amount)}` : "",
      preview?.expirationDate ? `Expira ${preview.expirationDate}` : "",
      preview?.gloss ? `Glosa: ${preview.gloss}` : "",
    ].filter(Boolean);
    return partes.join("\n");
  }, [qrPreview, factura, cod]);

  const dataUrlToFile = useCallback(async (dataUrl, fileName = "qr-factura.png") => {
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    return new File([blob], fileName, { type: blob.type || "image/png" });
  }, []);

  const descargarQrPreview = useCallback(async (preview = qrPreview) => {
    if (!preview?.dataUrl) return;
    const link = document.createElement("a");
    link.href = preview.dataUrl;
    link.download = `qr-factura-${preview.label || cod || "trs"}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [qrPreview, cod]);

  const compartirQrPreview = useCallback(async (preview = qrPreview) => {
    if (!preview?.dataUrl) return;
    try {
      const file = await dataUrlToFile(preview.dataUrl, `qr-factura-${preview.label || cod || "trs"}.png`);
      const shareData = {
        title: `QR factura ${factura?.num_fac || factura?.coddos || cod || ""}`.trim(),
        text: buildQrShareText(preview),
        files: [file],
      };

      if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
        await navigator.share(shareData);
        return;
      }

      await descargarQrPreview(preview);
      showToast?.("Tu navegador no permite compartir el QR directo. Se descargo la imagen para enviarla manualmente.", "info");
    } catch (e) {
      showToast?.(e?.message || "No se pudo compartir el QR", "error");
    }
  }, [qrPreview, dataUrlToFile, cod, factura, buildQrShareText, descargarQrPreview, showToast]);

  const compartirQrPorWhatsapp = useCallback(async (preview = qrPreview) => {
    if (!preview?.dataUrl) return;
    const text = buildQrShareText(preview);
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, "_blank", "noopener,noreferrer");

    if (navigator.share) {
      showToast?.("Se abrio WhatsApp con el mensaje. Si tu navegador lo permite, tambien podes usar Compartir para enviar la imagen del QR.", "info");
    } else {
      showToast?.("Se abrio WhatsApp con el mensaje. La imagen del QR podes descargarla y adjuntarla manualmente.", "info");
    }
  }, [qrPreview, buildQrShareText, showToast]);

  const guardarManual = async () => {
    if (!form.monto || Number(form.monto) <= 0) return showToast?.("Ingrese un monto valido", "warning");
    if (!form.tipo_ingreso) return showToast?.("Seleccione el tipo de ingreso", "warning");
    if (!form.banco_deposito) return showToast?.("Seleccione el banco", "warning");
    if (!String(form.cod_banco || "").trim()) return showToast?.("Ingrese el codigo de banco", "warning");
    setSaving(true);
    try {
      const payload = {
        ...form,
        banco_pago: form.banco_pago || form.banco_deposito,
      };
      const res = await svc.registrarPagoManual(cod, payload);
      const data = unwrapData(res);
      showToast?.("Pago registrado");
      await cargarPagos();
      onSuccess?.(data);
      setForm(buildDefaultForm());
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudo registrar el pago", "error");
    } finally {
      setSaving(false);
    }
  };

  const guardarExtracto = async () => {
    if (!extractoId) return showToast?.("Seleccione un pago del extracto", "warning");
    setSaving(true);
    try {
      const res = await svc.registrarPagoExtracto(cod, {
        id_extracto: extractoId,
        razonpago: form.razonpago,
        tipo_ingreso: form.tipo_ingreso,
        cobrador: form.cobrador,
        sucursal: form.sucursal,
        obs: form.obs,
      });
      const data = unwrapData(res);
      showToast?.("Pago vinculado");
      await Promise.all([cargarPagos(), cargarExtractos()]);
      onSuccess?.(data);
      setExtractoId("");
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudo vincular el pago", "error");
    } finally {
      setSaving(false);
    }
  };

  const eliminarPago = async (codPago) => {
    if (!window.confirm("Desea eliminar este pago?")) return;
    setSaving(true);
    try {
      const res = await svc.eliminarPagoFactura(cod, codPago);
      const data = unwrapData(res);
      showToast?.("Pago eliminado");
      await cargarPagos();
      onSuccess?.(data);
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudo eliminar el pago", "error");
    } finally {
      setSaving(false);
    }
  };

  const generarQr = async () => {
    if (!qrForm.amount || Number(qrForm.amount) <= 0) {
      return showToast?.("Ingrese un monto valido para el QR", "warning");
    }
    if (!qrForm.expirationDate) {
      return showToast?.("Seleccione la fecha de expiracion del QR", "warning");
    }
    setSaving(true);
    try {
      const res = await svc.generarFacturaQr(cod, {
        amount: qrForm.amount,
        expirationDate: qrForm.expirationDate,
        gloss: qrForm.gloss,
        additionalData: qrForm.additionalData,
        singleUse: qrForm.singleUse !== false,
      });
      const data = unwrapData(res) || {};
      setQrResumen({
        actual: data.actual || null,
        historial: Array.isArray(data.historial) ? data.historial : [],
        eventos: Array.isArray(data.eventos) ? data.eventos : [],
        stats: data.stats || { total: 0 },
      });
      setMostrarQrGenerado(true);
      setQrPreview(buildQrPreview(data.actual));
      showToast?.("QR generado correctamente");
      try {
        const facturaRes = await svc.getFactura(cod);
        onSuccess?.({ factura: unwrapData(facturaRes) });
      } catch {
        onSuccess?.();
      }
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudo generar el QR", "error");
    } finally {
      setSaving(false);
    }
  };

  const actualizarEstadoQr = async () => {
    setSaving(true);
    try {
      const res = await svc.actualizarEstadoFacturaQr(cod);
      const data = unwrapData(res) || {};
      setQrResumen({
        actual: data.actual || null,
        historial: Array.isArray(data.historial) ? data.historial : [],
        eventos: Array.isArray(data.eventos) ? data.eventos : [],
        stats: data.stats || { total: 0 },
      });
      const estadoActual = String(data.actual?.estado || "").toLowerCase();
      setMostrarQrGenerado(!!data.actual?.qr_data_url && !["cancelado", "expirado"].includes(estadoActual));
      setQrPreview((prev) => {
        if (!prev) return null;
        const nextPreview = buildQrPreview(data.actual);
        return nextPreview || prev;
      });
      showToast?.("Estado QR actualizado");
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudo actualizar el estado del QR", "error");
    } finally {
      setSaving(false);
    }
  };

  const cancelarQr = async () => {
    if (!window.confirm("Desea cancelar el QR actual?")) return;
    setSaving(true);
    try {
      const res = await svc.cancelarFacturaQr(cod);
      const data = unwrapData(res) || {};
      setQrResumen({
        actual: data.actual || null,
        historial: Array.isArray(data.historial) ? data.historial : [],
        eventos: Array.isArray(data.eventos) ? data.eventos : [],
        stats: data.stats || { total: 0 },
      });
      setMostrarQrGenerado(false);
      setQrPreview(null);
      showToast?.("QR cancelado");
    } catch (e) {
      showToast?.(e?.response?.data?.message || "No se pudo cancelar el QR", "error");
    } finally {
      setSaving(false);
    }
  };

  // Cada pago es una tarjeta (sin scroll horizontal): arriba lo principal (registro, fecha, montos) y debajo solo los datos que tienen valor.
  // Pagos que comparten el abono del extracto de un pago: { pago, filtro } (por extracto, o por código de banco en pagos manuales).
  const [relacionados, setRelacionados] = useState(null);
  // Con acceso a Egresos y un pago vinculado a un extracto se abre la misma vista del extracto (pagos, recibos, cheques y si cuadra); si no, la lista simple de pagos.
  const accedeAEgresos = ["admin", "gerente", "conta"].includes(String(user?.role || "").toLowerCase());
  const abrirRelacionados = (p) => {
    if (accedeAEgresos && Number(p.vinculo) > 0) {
      setRelacionados({
        movimiento: { id: Number(p.vinculo), fecha: p.extracto_fecha || p.fecha_deposito, banco: p.extracto_banco || p.banco_deposito_nombre, cod_transaccion: p.extracto_cod_transaccion || p.cod_banco, credito: p.extracto_credito, debito: 0 },
      });
      return;
    }
    setRelacionados({
      pago: p.cod,
      filtro: Number(p.vinculo) > 0 ? { vinculo: p.vinculo } : { cod_banco: p.cod_banco, banco_deposito: p.banco_deposito },
    });
  };

  const campoPago = (p) => [
    ["Forma de pago", p.forma_pago],
    ["Tipo de ingreso", getTipoIngresoLabel(p.tipo_ingreso)],
    ["Nro. cheque", p.numcheque],
    ["Nro. recibo", p.numrecibo],
    ["Banco de pago", p.banco_pago_nombre || p.banco_pago],
    ["Cód. banco", p.cod_banco],
    ["Banco de depósito", p.banco_deposito_nombre || p.banco_deposito],
    ["Fecha de depósito", String(p.fecha_deposito || "").slice(0, 10)],
    ["Razón de pago", p.razonpago],
    ["Cobrador", p.cobrador],
    ["Observaciones", p.obs],
    ["Descripción del extracto", p.extracto_descripcion || p.obs],
    ["Adicional del extracto", p.extracto_adicional],
  ].filter(([, v]) => v !== undefined && v !== null && String(v).trim() !== "" && String(v).trim() !== "-");

  const pagosRealizadosTable = (
    <Box sx={{ mt: 1, mb: 1, display: "grid", gap: 0.9, maxHeight: 340, overflowY: "auto", pr: 0.25 }}>
      {loading ? (
        <Box sx={{ p: 1 }}><CircularProgress size={16} /></Box>
      ) : pagos.length ? pagos.map((p) => {
        const usd = Number(p.tipo_pago) === 1 ? money(Number(p.monto || 0) / Number(p.moneda || 1)) : null;
        return (
          <Paper key={p.cod} variant="outlined" sx={{ ...warningPanelSx, overflow: "hidden" }}>
            <Box sx={{ ...warningHeadSx, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", px: 1, py: 0.5 }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 800 }}>Pago #{p.cod}</Typography>
              <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>{String(p.fecha_pago || "").slice(0, 10)}</Typography>
              <Box sx={{ ml: "auto", display: "flex", alignItems: "baseline", gap: 1 }}>
                {usd && <Typography sx={{ fontSize: "0.74rem", color: "text.secondary" }}>$us {usd}</Typography>}
                <Typography sx={{ fontSize: "0.95rem", fontWeight: 800 }}>Bs {money(p.monto)}</Typography>
              </Box>
              {puedeEliminarPago && (
                <Tooltip title="Eliminar pago">
                  <span>
                    <IconButton size="small" color="error" onClick={() => eliminarPago(p.cod)} disabled={saving} aria-label="Eliminar pago">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(165px, 1fr))", gap: 0.75, p: 1 }}>
              {campoPago(p).map(([etiqueta, valor]) => (
                <Box key={etiqueta} sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: "0.66rem", color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3 }}>{etiqueta}</Typography>
                  {etiqueta === "Cód. banco" ? (
                    <Tooltip title="Ver todos los pagos de este abono del extracto">
                      <Typography
                        component="button" type="button" onClick={() => abrirRelacionados(p)}
                        sx={{ all: "unset", cursor: "pointer", fontSize: "0.8rem", fontWeight: 700, color: "primary.main", textDecoration: "underline", overflowWrap: "anywhere", "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main" } }}
                      >
                        {valor}
                      </Typography>
                    </Tooltip>
                  ) : (
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, overflowWrap: "anywhere" }}>{valor}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Paper>
        );
      }) : (
        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", p: 1 }}>Sin pagos registrados.</Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreenMobile}
      PaperProps={{ sx: { m: { xs: 0, sm: 1 } } }}
    >
      <DialogTitle sx={{ px: 1.5, py: 1, fontSize: "0.9rem", fontWeight: 800 }}>
        Pago de factura #{cod}
      </DialogTitle>
      <DialogContent sx={{ p: 1.25 }}>
        <Box sx={{ display: "flex", gap: 0.75, mb: 1, flexWrap: "wrap" }}>
          <Chip size="small" label={`Total Bs ${money(factura?.monto_total)}`} />
          <Chip size="small" color="warning" label={`Saldo Bs ${money(factura?.saldo)}`} />
          <Chip size="small" label={factura?.fac_Nombre || "-"} />
        </Box>

        {pagosRealizadosTable}

        {facturaPagada && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}>
            <Box
              component="img"
              src={facturaPagadaImg}
              alt="Factura pagada"
              sx={{ maxWidth: 220, width: "45%", minWidth: 130, objectFit: "contain" }}
            />
          </Box>
        )}

        {!facturaPagada && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ minHeight: 30, mb: 1, "& .MuiTab-root": { minHeight: 30, py: 0, fontSize: "0.75rem" } }}>
              <Tab label="Seleccionar pago" />
              <Tab label="Pago manual" />
              <Tab label="Cobro QR" />
            </Tabs>

            {tab === 1 ? (
              <Grid container spacing={0.75}>
            <Grid item xs={6} md={2}>
              <TextField select fullWidth size="small" label="Pago en" sx={compactFieldSx} value={form.pago_moneda || "bolivianos"} onChange={(e) => handleForm("pago_moneda", e.target.value)}>
                <MenuItem value="bolivianos">Bolivianos</MenuItem>
                <MenuItem value="dolares">Dolares</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField select fullWidth size="small" label="Forma" sx={compactFieldSx} value={form.forma_pago || "Efectivo"} onChange={(e) => handleForm("forma_pago", e.target.value)}>
                <MenuItem value="Efectivo">Efectivo</MenuItem>
                <MenuItem value="Cheque">Cheque</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Cambio" sx={compactFieldSx} inputProps={{ inputMode: "decimal" }} value={form.moneda || ""} onChange={(e) => handleForm("moneda", e.target.value)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField required fullWidth size="small" label="Monto" sx={compactFieldSx} inputProps={{ inputMode: "decimal" }} value={form.monto || ""} onChange={(e) => handleForm("monto", e.target.value)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Fecha cobro" type="date" sx={compactFieldSx} value={form.fecha_pago || ""} onChange={(e) => handleForm("fecha_pago", e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Recibo" sx={compactFieldSx} value={form.numrecibo || ""} onChange={(e) => handleForm("numrecibo", e.target.value)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Cheque" sx={compactFieldSx} value={form.numcheque || ""} onChange={(e) => handleForm("numcheque", e.target.value)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField required select fullWidth size="small" label="Tipo ingreso" sx={compactFieldSx} value={form.tipo_ingreso || "1"} onChange={(e) => handleForm("tipo_ingreso", e.target.value)}>
                {TIPOS_INGRESO.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2.5}>
              <TextField required select fullWidth size="small" label="Banco deposito" sx={compactFieldSx} value={form.banco_deposito || ""} onChange={(e) => handleForm("banco_deposito", e.target.value)}>
                <MenuItem value="">Sin banco</MenuItem>
                {bancos.map((b) => <MenuItem key={b.id} value={b.id}>{b.nombre}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField required fullWidth size="small" label="Cod banco" sx={compactFieldSx} value={form.cod_banco || ""} onChange={(e) => handleForm("cod_banco", e.target.value)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Fecha deposito" type="date" sx={compactFieldSx} value={form.fecha_deposito || ""} onChange={(e) => handleForm("fecha_deposito", e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Cobrador" sx={compactFieldSx} value={form.cobrador || ""} onChange={(e) => handleForm("cobrador", e.target.value)} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField fullWidth size="small" label="Sucursal" sx={compactFieldSx} value={form.sucursal || ""} onChange={(e) => handleForm("sucursal", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth size="small" label="Razon pago" sx={compactFieldSx} value={form.razonpago || ""} onChange={(e) => handleForm("razonpago", e.target.value)} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField fullWidth size="small" label="Observacion" sx={compactFieldSx} value={form.obs || ""} onChange={(e) => handleForm("obs", e.target.value)} />
            </Grid>
              </Grid>
            ) : tab === 0 ? (
              <Box>
                <Grid container spacing={0.75} sx={{ mb: 0.75 }}>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Buscar extracto" sx={compactFieldSx} value={buscarExtracto} onChange={(e) => { setBuscarExtracto(e.target.value); setExtractosPage(0); }} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth size="small" label="Observacion" sx={compactFieldSx} value={form.obs || ""} onChange={(e) => handleForm("obs", e.target.value)} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Alert severity="info" sx={{ py: 0, fontSize: "0.72rem" }}>
                      Seleccione un extracto disponible. Se aplicara automaticamente el menor entre saldo de factura y saldo disponible del extracto.
                    </Alert>
                  </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ maxHeight: 245, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ "& th": { py: 0.45, px: 0.6, fontSize: "0.72rem" } }}>
                        <TableCell padding="checkbox" />
                        <TableCell>Cod</TableCell>
                        <TableCell>Cod transaccion</TableCell>
                        <TableCell>Banco</TableCell>
                        <TableCell>Descripcion</TableCell>
                        <TableCell>Fecha</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell align="right">Saldo disponible</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {extractosDisponibles.length ? extractosDisponibles.map((e) => {
                        const seleccionado = String(extractoId) === String(e.id);
                        const saldoDisponible = saldoDisponibleExtracto(e);
                        return (
                          <TableRow key={e.id} hover selected={seleccionado} onClick={() => seleccionarExtracto(e)} sx={{ cursor: "pointer", "& td": { py: 0.35, px: 0.6, fontSize: "0.74rem" } }}>
                            <TableCell padding="checkbox">
                              <Checkbox size="small" checked={seleccionado} onChange={() => seleccionarExtracto(e)} />
                            </TableCell>
                            <TableCell>{e.id}</TableCell>
                            <TableCell>{e.cod_transaccion || "-"}</TableCell>
                            <TableCell>{e.banco || e.oficina || "-"}</TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: "0.74rem" }}>{e.descripcion || "-"}</Typography>
                              <Typography sx={{ fontSize: "0.68rem", color: "text.secondary" }}>{e.adicional || e.cod_transaccion || "-"}</Typography>
                            </TableCell>
                            <TableCell>{String(e.fecha || "").slice(0, 10)}</TableCell>
                            <TableCell align="right">{money(e.credito)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 800, color: saldoDisponible > 0 ? "error.main" : "text.secondary" }}>{money(saldoDisponible)}</TableCell>
                          </TableRow>
                        );
                      }) : (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ fontSize: "0.74rem", color: "text.secondary" }}>Sin extractos disponibles.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>
                <TablePagination
                  component="div"
                  count={extractosTotal}
                  page={extractosPage}
                  onPageChange={(_, page) => setExtractosPage(page)}
                  rowsPerPage={extractosPageSize}
                  onRowsPerPageChange={(e) => {
                    setExtractosPageSize(parseInt(e.target.value, 10));
                    setExtractosPage(0);
                  }}
                  rowsPerPageOptions={[10, 20, 30, 50]}
                  labelRowsPerPage="Limite"
                  sx={{ "& .MuiTablePagination-toolbar": { minHeight: 32 }, "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontSize: "0.72rem" } }}
                />
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Alert severity="info" sx={{ py: 0.25, fontSize: "0.74rem" }}>
                  Cada generacion crea un registro historico independiente. Las acciones del QR se gestionan desde el listado inferior.
                </Alert>

                {qrPreview?.dataUrl ? (
                  <Paper variant="outlined" sx={{ p: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 0.75 }}>
                    <Chip
                      size="small"
                      color={qrStatusColor(qrPreview?.estado)}
                      label={`${mostrarQrGenerado ? "QR generado" : "QR"} ${qrPreview?.estado || ""}`.trim()}
                    />
                    <Box
                      component="img"
                      src={qrPreview.dataUrl}
                      alt={`QR factura ${cod}`}
                      sx={{ width: 150, maxWidth: "100%", border: "1px solid", borderColor: "divider", borderRadius: 1, p: 0.75, bgcolor: "#fff" }}
                    />
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", justifyContent: "center" }}>
                      <Button size="small" variant="outlined" startIcon={<VisibilityOffIcon />} onClick={() => { setQrPreview(null); setMostrarQrGenerado(false); }}>
                        Ocultar QR
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => descargarQrPreview()}>
                        Descargar
                      </Button>
                      <Button size="small" variant="outlined" startIcon={<ShareIcon />} onClick={() => compartirQrPreview()}>
                        Compartir
                      </Button>
                      <Button size="small" variant="outlined" color="success" startIcon={<WhatsAppIcon />} onClick={() => compartirQrPorWhatsapp()}>
                        WhatsApp
                      </Button>
                    </Box>
                  </Paper>
                ) : null}

                <Grid container spacing={0.75}>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Monto Bs"
                      sx={compactFieldSx}
                      inputProps={{ inputMode: "decimal" }}
                      value={qrForm.amount || ""}
                      onChange={(e) => handleQrForm("amount", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      label="Expira"
                      sx={compactFieldSx}
                      value={qrForm.expirationDate || ""}
                      onChange={(e) => handleQrForm("expirationDate", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Glosa"
                      sx={compactFieldSx}
                      value={qrForm.gloss || ""}
                      onChange={(e) => handleQrForm("gloss", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Dato adicional"
                      sx={compactFieldSx}
                      value={qrForm.additionalData || ""}
                      onChange={(e) => handleQrForm("additionalData", e.target.value)}
                    />
                  </Grid>
                </Grid>

                <Paper variant="outlined" sx={{ maxHeight: 180, overflow: "auto" }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow sx={{ "& th": { py: 0.45, px: 0.6, fontSize: "0.72rem" } }}>
                        <TableCell>ID</TableCell>
                        <TableCell>Estado</TableCell>
                          <TableCell>BNB</TableCell>
                        <TableCell align="right">Monto</TableCell>
                        <TableCell>Expira</TableCell>
                        <TableCell>Creado</TableCell>
                        <TableCell>Operador</TableCell>
                        <TableCell>Error</TableCell>
                        <TableCell>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {qrLoading ? (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ py: 2 }}>
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                              <CircularProgress size={22} />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : qrResumen.historial?.length ? qrResumen.historial.map((item) => {
                        const qrEstado = String(item.estado || "").toLowerCase();
                        const puedeAbrirQr = !!item.qr_data_url;
                        const puedeActualizarQr = !!item.es_actual && qrEstado !== "cancelado";
                        const puedeCancelarQr = !!item.es_actual && !["cancelado", "pagado"].includes(qrEstado);
                        return (
                          <TableRow key={item.id} selected={!!item.es_actual} sx={{ "& td": { py: 0.35, px: 0.6, fontSize: "0.73rem" } }}>
                            <TableCell>{item.provider_qr_id || item.id}</TableCell>
                            <TableCell>
                              <Chip size="small" color={qrStatusColor(item.estado)} label={item.estado || "-"} sx={{ height: 18, fontSize: "0.65rem" }} />
                            </TableCell>
                            <TableCell>{item.provider_status_text || item.provider_status_id || "-"}</TableCell>
                            <TableCell align="right">{money(item.amount)}</TableCell>
                            <TableCell>{item.expiration_date || "-"}</TableCell>
                            <TableCell>{String(item.created_at || "").slice(0, 19).replace("T", " ") || "-"}</TableCell>
                            <TableCell>{item.created_by || "-"}</TableCell>
                            <TableCell sx={{ color: item.error_message ? "error.main" : "text.secondary" }}>{item.error_message || "-"}</TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<QrCode2Icon />}
                                  onClick={() => {
                                    if (!puedeAbrirQr) return;
                                    setMostrarQrGenerado(false);
                                    setQrPreview(buildQrPreview(item));
                                  }}
                                  disabled={!puedeAbrirQr}
                                >
                                  Ver QR
                                </Button>
                                {puedeActualizarQr ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={actualizarEstadoQr}
                                    disabled={saving}
                                  >
                                    Estado
                                  </Button>
                                ) : null}
                                {puedeCancelarQr ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={cancelarQr}
                                    disabled={saving}
                                  >
                                    Cancelar
                                  </Button>
                                ) : null}
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      }) : (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ fontSize: "0.74rem", color: "text.secondary" }}>
                            Aun no existen QRs generados para esta factura.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Paper>

              </Box>
            )}
          </>
        )}

      </DialogContent>
      <DialogActions sx={{ p: 1 }}>
        <Button size="small" onClick={onClose}>Cerrar</Button>
        {!facturaPagada && (
        <Button size="small" variant="contained" color="success" disableElevation onClick={tab === 0 ? guardarExtracto : tab === 1 ? guardarManual : generarQr} disabled={saving || loading || qrLoading || Number(factura?.saldo || 0) <= 0}>
            {saving ? "Guardando..." : tab === 0 ? "Vincular pago" : tab === 1 ? "Registrar pago" : "Generar QR"}
          </Button>
        )}
      </DialogActions>
      <PagosRelacionadosDialog open={!!relacionados?.filtro} filtro={relacionados?.filtro} pagoActual={relacionados?.pago} onClose={() => setRelacionados(null)} />
      <MovimientoRelacionesDialog movimiento={relacionados?.movimiento} tipo="credito" onClose={() => setRelacionados(null)} />
    </Dialog>
  );
};

// Fila de solo lectura de una linea fiscal (la edicion vive en EditarFacturaModal)
const DetalleRow = ({ d }) => (
  <TableRow hover sx={{ "& .MuiTableCell-root": { py: 0.35, px: 0.5, fontSize: "0.76rem" } }}>
    <TableCell align="center" sx={{ fontWeight: 600 }}>{d.cantidad}</TableCell>
    <TableCell color="text.secondary">{d.concepto || d.tipo || "-"}</TableCell>
    <TableCell sx={{ fontWeight: 500 }}>{d.detalle}</TableCell>
    <TableCell align="center">
      <Chip
        label={d.mostrar_cuota || "NO"}
        size="small"
        variant="outlined"
        color={d.mostrar_cuota === "SI" ? "primary" : "default"}
        sx={{ height: 16, fontSize: "0.65rem", fontWeight: 700 }}
      />
    </TableCell>
    <TableCell color="text.secondary">{d.mes_servicio || "-"} {d.year || ""}</TableCell>
    <TableCell align="right">{Number(d.precio_unitario || 0).toFixed(2)}</TableCell>
    <TableCell align="right" sx={{ color: Math.abs(parseDecimal(d.descuento_interno)) > 0 ? "error.main" : "inherit" }}>
      {Math.abs(parseDecimal(d.descuento_interno)) > 0 ? `-${Math.abs(parseDecimal(d.descuento_interno)).toFixed(2)}` : "0.00"}
    </TableCell>
    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: "rgba(0,0,0,0.01)" }}>
      {Number(d.subtotal || 0).toFixed(2)}
    </TableCell>
  </TableRow>
);

// Modal principal
// Fila de solo lectura de un concepto interno (la edicion vive en EditarFacturaModal)
const DetalleGrupoRow = ({ g }) => {
  const subtotal = (parseDecimal(g.cantidad) * parseDecimal(g.precio_unitario_base)) - parseDecimal(g.descuento_interno);
  const lineas = Array.isArray(g.lineas) ? g.lineas : [];

  return (
    <TableRow hover sx={{ "& .MuiTableCell-root": { py: 0.35, px: 0.6, fontSize: "0.74rem" } }}>
      <TableCell align="right">{Number(g.cantidad || 0)}</TableCell>
      <TableCell>
        <Typography variant="caption" sx={{ fontWeight: 800 }}>{g.descripcion_interna || "-"}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.67rem" }}>
          {lineas.map((l) => `${Number(l.porcentaje || 100)}% ${stripCuotaDetalle(l.detalle)}`).join(" | ")}
        </Typography>
      </TableCell>
      <TableCell align="right">{money(g.precio_unitario_base)}</TableCell>
      <TableCell align="right">{money(g.descuento_interno)}</TableCell>
      <TableCell align="center">{(g.mostrar_cuota || "SI") === "SI" ? `${g.mes_servicio || "-"} ${g.year || ""}` : "-"}</TableCell>
      <TableCell align="right" sx={{ fontWeight: 800 }}>{money(subtotal)}</TableCell>
    </TableRow>
  );
};

const DetalleFacturaModal = ({ open = true, onClose, cod, onSuccess, pageMode = false }) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [factura,     setFactura]     = useState(null);
  const [detalles,    setDetalles]    = useState([]);
  const [detalleGrupos, setDetalleGrupos] = useState([]);
  const [facItems,    setFacItems]    = useState([]);
  const [reglasDetalle, setReglasDetalle] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [actionLoading, setActionLoading] = useState("");
  const [motivosAnulacion, setMotivosAnulacion] = useState([]);
  const [anularDialogOpen, setAnularDialogOpen] = useState(false);
  const [codigoMotivo, setCodigoMotivo] = useState("");
  const [editarDialogOpen, setEditarDialogOpen] = useState(false);
  const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
  const [toast,       setToast]       = useState({ open: false, message: "", severity: "success", details: null });
  const [toastDetailsOpen, setToastDetailsOpen] = useState(false);

  const bloqueada = factura?.pre_fac === "no" || factura?.estado === "X";
  const anulada = factura?.estado === "X";
  const esPrefactura = factura?.pre_fac === "si";
  const esFactura = factura?.pre_fac === "no";
  const tieneNumeroPrefactura = Boolean(
    factura?.coddos &&
    String(factura.coddos).trim() !== "" &&
    String(factura.coddos) !== "0"
  );
  const tienePagos = Number(factura?.pagos_registrados || 0) > 0;
  const tieneDetalleInterno = detalleGrupos.length > 0;
  const tieneReglasInternasConfiguradas = (Array.isArray(reglasDetalle) ? reglasDetalle : [])
    .some((concepto) => Array.isArray(concepto?.reglas) && concepto.reglas.length > 0);
  const debeMostrarConceptosInternos =
    tieneDetalleInterno ||
    (!bloqueada && detalles.length === 0 && tieneReglasInternasConfiguradas);

  const cargar = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const resFac = await svc.getFactura(cod);
      const facturaData  = resFac.data?.data  ?? resFac.data;
      const [resDet, resGrupos, resItems, resReglas] = await Promise.all([
        axios.get(`/api/facturas/${cod}/detalles`),
        axios.get(`/api/facturas/${cod}/detalle-grupos`),
        svc.getFacItems({ cod_servicio: facturaData?.cod_servicio }),
        facturaData?.cod_servicio ? svc.getReglasDetalle({ cod_servicio: facturaData.cod_servicio }) : Promise.resolve({ data: [] }),
      ]);
      const detallesData = resDet.data?.data  ?? resDet.data;
      const gruposData = resGrupos.data?.data ?? resGrupos.data;
      const facItemsData = resItems.data?.data ?? resItems.data;
      const reglasData = resReglas.data?.data ?? resReglas.data;
  
      setFactura(facturaData);
      setDetalles(Array.isArray(detallesData) ? detallesData : []);
      setDetalleGrupos(normalizeDetalleGrupos(gruposData, detallesData));
      setFacItems(Array.isArray(facItemsData) ? facItemsData : []);
      setReglasDetalle(Array.isArray(reglasData) ? reglasData : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Error al cargar la factura");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [cod]);

  useEffect(() => { if (cod) cargar(); }, [cod, cargar]);

  const refrescar = async (options = {}) => {
    await cargar(options);
    onSuccess?.();
  };

  const showToast = (message, severity = "success", details = null) => {
    setToast({ open: true, message, severity, details });
    setToastDetailsOpen(false);
  };

  const ejecutarAccion = async (key, fn, successMessage) => {
    setActionLoading(key);
    try {
      await fn();
      await refrescar();
      if (successMessage) showToast(successMessage);
    } catch (e) {
      showToast(
        e?.response?.data?.message || e?.message || "No se pudo completar la accion",
        "error",
        e?.response?.data?.details?.hippoBody ? { hippoBody: e.response.data.details.hippoBody } : null
      );
    } finally {
      setActionLoading("");
    }
  };

  const handleAnularPrefactura = () => {
    if (!window.confirm("Desea anular esta prefactura?")) return;
    ejecutarAccion("anular-prefactura", () => svc.anularPrefactura(cod));
  };

  const handleEmitirPrefactura = () => {
    if (!window.confirm("Desea emitir esta pre-factura como factura?")) return;
    ejecutarAccion("emitir-factura", () => svc.emitir(cod, {}));
  };

  const handleAnularFactura = async () => {
    setAnularDialogOpen(true);
    if (motivosAnulacion.length) return;
    setActionLoading("motivos");
    try {
      const res = await svc.getMotivosAnulacion();
      const raw = res.data?.data ?? res.data;
      const motivos = (Array.isArray(raw) ? raw : [])
        .map(normalizeMotivoAnulacion)
        .filter((m) => m.codigo && m.descripcion);
      setMotivosAnulacion(motivos);
      if (!codigoMotivo && motivos[0]?.codigo) setCodigoMotivo(motivos[0].codigo);
    } catch (e) {
      alert(e?.response?.data?.message || "No se pudieron cargar los motivos de de anulacion");
    } finally {
      setActionLoading("");
    }
  };

  const confirmarAnularFactura = () => {
    if (!codigoMotivo) return alert("Seleccione un motivo de anulacion");
    if (!window.confirm("Desea anular esta factura? Esta accion no se puede deshacer.")) return;
    setAnularDialogOpen(false);
    ejecutarAccion("anular-factura", () => svc.anular(cod, Number(codigoMotivo)));
  };

  const handlePagar = () => {
    setPagoDialogOpen(true);
  };

  const handleEnviarCorreo = () => {
    const emailDefault = factura?.correo_legal || "";
    const email = window.prompt("Correo electronico para enviar la factura", emailDefault);
    if (!email) return;
    ejecutarAccion("email", () => svc.enviarFacturaEmail(cod, email.trim()), "Factura enviada por correo");
  };

  const handleImprimir = async (tipoDocumento = esPrefactura ? "prefactura" : "factura") => {
    const loadingKey = tipoDocumento === "prefactura" ? "imprimir-prefactura" : "imprimir-factura";
    const pdfWindow = window.open("", "_blank");

    if (!pdfWindow) {
      alert("El navegador bloqueó la nueva pestaña. Habilita las ventanas emergentes para imprimir el documento.");
      return;
    }

    pdfWindow.opener = null;
    pdfWindow.document.title = "Cargando PDF...";
    pdfWindow.document.body.innerHTML =
      '<div style="font-family:Arial,sans-serif;padding:24px;color:#334155">Cargando PDF...</div>';

    setActionLoading(loadingKey);
    try {
      const { data } = tipoDocumento === "prefactura"
        ? await svc.imprimirPrefactura(cod)
        : await svc.imprimirFactura(cod);
      const pdfUrl = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      pdfWindow.location.replace(pdfUrl);
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
    } catch (e) {
      pdfWindow.close();
      alert(e?.response?.data?.message || "No se pudo imprimir el documento");
    } finally {
      setActionLoading("");
    }
  };

  const total = detalles.reduce(
    (acc, d) => acc + ((d.cantidad * (d.precio_unitario || 0)) - (d.descuento_interno || 0)),
    0
  );
  const totalConceptosInternos = detalleGrupos.reduce(
    (acc, grupo) =>
      acc +
      (
        parseDecimal(grupo.cantidad) * parseDecimal(grupo.precio_unitario_base) -
        parseDecimal(grupo.descuento_interno)
      ),
    0
  );
  const numeroDocumento = esPrefactura
    ? (factura?.coddos || factura?.cod || cod)
    : (factura?.num_fac || factura?.coddos || factura?.cod || cod);
  const tipoDocumentoLabel = esPrefactura ? "Pre-factura" : "Factura";

  const detalleContent = (
    <>
      {/* Contenedor titulo y acciones de arriba */}
      <DialogTitle
        sx={{
          px: { xs: 1.25, sm: 2 },
          py: 1,
          fontSize: "0.95rem",
          fontWeight: 700,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {tipoDocumentoLabel} Nro.
          <Typography component="span" variant="subtitle1" fontWeight={800} color="primary">
            #{numeroDocumento}
          </Typography>
          {factura && (
            <Chip
              label={
                anulada
                  ? `${esPrefactura ? "PRE-FACTURA" : "FACTURA"} ANULADA`
                  : esPrefactura
                    ? "PRE-FACTURA"
                    : "FACTURA"
              }
              size="small"
              color={anulada ? "error" : esPrefactura ? "warning" : "success"}
              sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, borderRadius: "4px" }}
            />
          )}
          {factura?.pagada === "si" && (
            <Chip icon={<CheckCircleIcon style={{ fontSize: 12, color: "#fff" }} />} label="PAGADA" size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: "success.dark", color: "#fff", borderRadius: "4px" }} />
          )}
        </Box>
        
        {/* Fila compacta de botones de accion */}
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {esFactura && !anulada && (
            <Tooltip title={tienePagos ? "No se puede anular porque tiene pagos registrados" : ""}>
              <span>
            <Button size="small" color="error" variant="contained" disableElevation startIcon={<CancelIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={handleAnularFactura} disabled={!!actionLoading || tienePagos}>
              {actionLoading === "anular-factura" ? "Anulando..." : "Anular factura"}
            </Button>
              </span>
            </Tooltip>
          )}
          {esPrefactura && !anulada && (
            <Tooltip title={tienePagos ? "No se puede anular porque tiene pagos registrados" : ""}>
              <span>
            <Button size="small" color="error" variant="contained" disableElevation startIcon={<CancelIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={handleAnularPrefactura} disabled={!!actionLoading || tienePagos}>
              {actionLoading === "anular-prefactura" ? "Anulando..." : "Anular prefactura"}
            </Button>
              </span>
            </Tooltip>
          )}
          {esPrefactura && !anulada && (
            <Button size="small" color="primary" variant="contained" disableElevation startIcon={<SendIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={handleEmitirPrefactura} disabled={!!actionLoading}>
              {actionLoading === "emitir-factura" ? "Emitiendo..." : "Emitir factura"}
            </Button>
          )}
          {esFactura && (
            <Button size="small" variant="outlined" startIcon={<PrintIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={() => handleImprimir("factura")} disabled={!!actionLoading}>
              {actionLoading === "imprimir-factura" ? "Imprimiendo..." : "Imprimir factura"}
            </Button>
          )}
          {(esPrefactura || tieneNumeroPrefactura) && (
            <Button size="small" variant="outlined" startIcon={<PrintIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={() => handleImprimir("prefactura")} disabled={anulada || !!actionLoading}>
              {actionLoading === "imprimir-prefactura" ? "Imprimiendo..." : "Imprimir pre-factura"}
            </Button>
          )}
          <Button size="small" variant="outlined" startIcon={<EmailIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={handleEnviarCorreo} disabled={!esFactura || anulada || !!actionLoading}>
            Correo
          </Button>
          {!!factura?.qr && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<QrCode2Icon />}
              sx={{ height: 24, fontSize: "0.68rem" }}
              onClick={() => window.open(factura.qr, "_blank", "noopener,noreferrer")}
            >
              Ver QR
            </Button>
          )}
          <Button size="small" color="success" variant="contained" disableElevation startIcon={<PaymentIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={handlePagar} disabled={anulada || !!actionLoading}>
            {factura?.pagada === "si" ? "Ver pagos" : "Pagar factura"}
          </Button>
          {(!bloqueada || (esFactura && !anulada)) && (
            <Button size="small" variant="outlined" startIcon={<EditIcon />} sx={{ height: 24, fontSize: "0.68rem" }} onClick={() => setEditarDialogOpen(true)} disabled={!!actionLoading}>
              Editar
            </Button>
          )}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 1.5, ...softPanelSx }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : error ? (
          <Alert severity="error" variant="filled">{error}</Alert>
        ) : factura ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            
            {/* Subseccion cabecera datos facturacion (solo lectura, se edita desde el dialog "Editar") */}
            <Paper variant="outlined" sx={{ p: 1, bgcolor: "background.paper", position: "relative" }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", textTransform: "uppercase", fontSize: "0.68rem", display: "block", mb: 0.5 }}>
                Datos del Cliente / Facturacion
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(12, minmax(0, 1fr))",
                  },
                  gap: 0.65,
                }}
              >
                {/* Fila 1: identificación del documento y fechas */}
                <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 2" } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>{esPrefactura ? "Nro. pre-factura" : "Nro. factura"}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 800, color: "primary.main", fontSize: "0.95rem" }}>
                    {esPrefactura ? (factura.coddos || factura.cod || "-") : (factura.num_fac || "-")}
                  </Typography>
                </Box>

                {esFactura ? (
                  <>
                    <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 2" } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Fecha de factura</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {fechaSola(factura.fecha_fac) || fechaSola(factura.fecha_emi) || "-"}
                      </Typography>
                    </Box>
                    <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 2" } }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Registrada el</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{fechaConHora(factura.fecha_emi) || "-"}</Typography>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 4" } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Pre-factura creada el</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {fechaConHora(factura.fecha_emi) || "-"}
                      <Typography component="span" sx={{ ml: 0.75, fontSize: "0.68rem", fontWeight: 400, color: "text.secondary" }}>(aún sin emitir)</Typography>
                    </Typography>
                  </Box>
                )}

                <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 2" } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Periodo</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {factura.mes || "-"} / {factura.year || "-"}
                  </Typography>
                </Box>

                <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 2" } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Sucursal</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{factura.sucursal || "-"}</Typography>
                </Box>

                <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 2" } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Tipo de venta</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{factura.venta || "-"}</Typography>
                </Box>

                {/* Fila 2: a quién se factura y cuánto */}
                <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 3" } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>NIT / CI</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                    {factura.nit || "-"}
                    <Typography component="span" sx={{ ml: 0.5, fontSize: "0.66rem", color: "text.secondary" }}>
                      ({TIPOS_DOCUMENTO.find((t) => t.codigo === Number(factura.tipo_documento_identidad))?.nombre || "NIT"})
                    </Typography>
                  </Typography>
                </Box>

                <Box sx={{ ...infoFieldSx, gridColumn: { md: "span 6" } }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Razón social</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
                    {factura.fac_Nombre || factura.razon_social || "-"}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...infoFieldSx,
                    gridColumn: { md: "span 3" },
                    textAlign: { md: "right" },
                    bgcolor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.action.selected
                        : "#ecfeff",
                    borderColor: (theme) =>
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.dark
                        : "#a5f3fc",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 600 }}>Total facturado (Bs.)</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 900, color: "primary.main", fontSize: "1.05rem", lineHeight: 1.25 }}>
                    {Number(factura.monto_total || 0).toFixed(2)}
                  </Typography>
                  {!anulada && (
                    factura.pagada === "si" ? (
                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "success.main" }}>Pagada completa</Typography>
                    ) : Number(factura.saldo) > 0 ? (
                      <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "warning.main" }}>Saldo pendiente: {Number(factura.saldo).toFixed(2)}</Typography>
                    ) : null
                  )}
                </Box>

                {factura.cuf && (
                  <Box sx={{ ...infoFieldSx, gridColumn: "1 / -1", display: "flex", alignItems: "flex-start", gap: 0.75 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ flex: "0 0 auto", pt: 0.1, fontSize: "0.66rem", fontWeight: 600 }}>CUF</Typography>
                    <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.68rem", wordBreak: "break-all" }}>
                      {factura.cuf}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Paper>

            {debeMostrarConceptosInternos && (
              <Paper variant="outlined" sx={{ bgcolor: "background.paper" }}>
                <Box sx={{ p: 0.75, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", ...warningPanelSx, gap: 1, flexWrap: "wrap" }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: "warning.main", fontSize: "0.68rem" }}>
                    CONCEPTOS INTERNOS ({detalleGrupos.length})
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
                    {detalleGrupos.length ? "Usa Editar para agregar/quitar conceptos" : "Agregue el primer concepto interno desde Editar para generar las lineas fiscales"}
                  </Typography>
                </Box>
                <TableContainer sx={{ overflowX: "auto" }}>
                  <Table size="small">
                    <TableHead sx={warningHeadSx}>
                      <TableRow sx={{ "& th": { py: 0.5, px: 0.6, fontSize: "0.72rem", fontWeight: 800 } }}>
                        <TableCell align="right" style={{ width: 75 }}>Cant.</TableCell>
                        <TableCell>Descripcion interna</TableCell>
                        <TableCell align="right" style={{ width: 100 }}>P. Base</TableCell>
                        <TableCell align="right" style={{ width: 95 }}>Descuento</TableCell>
                        <TableCell align="center" style={{ width: 180 }}>Periodo</TableCell>
                        <TableCell align="right" style={{ width: 95 }}>Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detalleGrupos.length ? (
                        <>
                          {detalleGrupos.map((g) => (
                            <DetalleGrupoRow key={g.id} g={g} />
                          ))}
                          <TableRow sx={warningPanelSx}>
                            <TableCell colSpan={5} align="right" sx={{ py: 0.55, fontWeight: 900, color: "warning.main", fontSize: "0.74rem" }}>
                              TOTAL CONCEPTOS INTERNOS
                            </TableCell>
                            <TableCell align="right" sx={{ py: 0.55, fontWeight: 900, color: "warning.main", fontSize: "0.78rem" }}>
                              {money(totalConceptosInternos)}
                            </TableCell>
                          </TableRow>
                        </>
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center" sx={{ py: 1.5, color: "text.secondary", fontSize: "0.76rem" }}>
                            Esta prefactura no tiene conceptos internos. Use Editar para crear el primero.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Tabla de detalles ultra compacta */}
            <Paper variant="outlined" sx={{ bgcolor: "background.paper" }}>
              <Box sx={{ p: 0.75, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}>
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.secondary", fontSize: "0.68rem" }}>LINEAS DE DETALLE ({detalles.length})</Typography>
                  {tieneDetalleInterno && (
                    <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontSize: "0.66rem" }}>
                      Se generan desde conceptos internos. Edite el concepto interno para recalcular estas lineas.
                    </Typography>
                  )}
                </Box>
              </Box>

              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small">
                  <TableHead sx={{ bgcolor: (theme) => theme.palette.mode === "dark" ? theme.palette.background.paper : theme.palette.grey[100] }}>
                    <TableRow sx={{ "& th": { py: 0.5, px: 0.6, fontSize: "0.72rem", fontWeight: 700, color: "text.primary" } }}>
                      <TableCell align="center" style={{ width: 55 }}>Cant.</TableCell>
                      <TableCell style={{ width: 118 }}>Tipo</TableCell>
                      <TableCell>Detalle Comercial</TableCell>
                      <TableCell align="center" style={{ width: 70 }}>Cuota?</TableCell>
                      <TableCell style={{ width: 145 }}>Periodo Serv.</TableCell>
                      <TableCell align="right" style={{ width: 85 }}>P. Unitario</TableCell>
                      <TableCell align="right" style={{ width: 80 }}>Descuento</TableCell>
                      <TableCell align="right" style={{ width: 90 }}>Subtotal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detalles.map((d) => (
                      <DetalleRow key={d.cod} d={d} />
                    ))}

                    {/* FILA DE TOTALES CALCULADOS */}
                    <TableRow sx={{ bgcolor: (theme) => theme.palette.action.hover }}>
                      <TableCell colSpan={7} align="right" sx={{ fontWeight: 700, fontSize: "0.75rem" }}>Suma Total Calculada (Bs.):</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: "0.8rem", color: "primary.main" }}>{total.toFixed(2)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        ) : null}
      </DialogContent>

      {!pageMode && (
        <DialogActions sx={{ p: 1, borderTop: "1px solid", borderColor: "divider", justifyContent: "flex-end" }}>
          <Button size="small" variant="outlined" onClick={onClose} color="inherit" sx={{ height: 26, fontSize: "0.75rem" }}>
            Cerrar Ventana
          </Button>
        </DialogActions>
      )}

      {/* Dialog de anulacion */}
      <Dialog open={anularDialogOpen} onClose={() => setAnularDialogOpen(false)}>
        <DialogTitle sx={{ p: 1.5, fontSize: "0.85rem", fontWeight: 700 }}>Motivo de Anulacion (SIN)</DialogTitle>
        <DialogContent sx={{ p: 1.5, minWidth: 290 }}>
          <TextField select fullWidth size="small" label="Seleccione el motivo" sx={compactFieldSx} value={codigoMotivo} onChange={(e) => setCodigoMotivo(e.target.value)}>
            {motivosAnulacion.map((m) => (
              <MenuItem key={m.codigo} value={m.codigo}>{m.codigo} - {m.descripcion}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button size="small" onClick={() => setAnularDialogOpen(false)}>Cancelar</Button>
          <Button size="small" color="error" variant="contained" disableElevation onClick={confirmarAnularFactura}>
            Confirmar Anulacion
          </Button>
      </DialogActions>
    </Dialog>

      <PagoFacturaDialog
        open={pagoDialogOpen}
        onClose={() => setPagoDialogOpen(false)}
        cod={cod}
        factura={factura}
        showToast={showToast}
        onSuccess={(data) => {
          if (data?.factura) {
            setFactura(data.factura);
            return;
          }
          refrescar({ silent: true });
        }}
      />

      <EditarFacturaModal
        open={editarDialogOpen}
        onClose={() => setEditarDialogOpen(false)}
        cod={cod}
        factura={factura}
        detalles={detalles}
        detalleGrupos={detalleGrupos}
        facItems={facItems}
        reglasDetalle={reglasDetalle}
        onSaved={() => { setEditarDialogOpen(false); refrescar(); }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={toast.details ? null : 3500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%", maxWidth: 720, alignItems: "flex-start" }}
        >
          <Box>
            <Box>{toast.message}</Box>
            {toast.details?.hippoBody && (
              <>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setToastDetailsOpen((v) => !v)}
                  sx={{ mt: 0.5, px: 0, minWidth: 0, fontSize: "0.72rem", textTransform: "none" }}
                >
                  {toastDetailsOpen ? "Ver menos" : "Ver mas"}
                </Button>
                <Collapse in={toastDetailsOpen}>
                  <Box
                    component="pre"
                    sx={{
                      mt: 0.75,
                      mb: 0,
                      p: 1,
                      maxHeight: 260,
                      overflow: "auto",
                      bgcolor: "rgba(0,0,0,0.28)",
                      borderRadius: 1,
                      fontSize: "0.68rem",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {formatJson(toast.details.hippoBody)}
                  </Box>
                </Collapse>
              </>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );

  if (pageMode) {
    return (
      <Box sx={{ p: { xs: 1, md: 1.5 } }}>
        <Paper variant="outlined" sx={{ overflow: "hidden", bgcolor: "background.paper" }}>
          {detalleContent}
        </Paper>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreenMobile}
      PaperProps={{ sx: { m: { xs: 0, sm: 1 }, maxHeight: { xs: "100%", sm: "calc(100% - 16px)" } } }}
    >
      {detalleContent}
    </Dialog>
  );
};

export default DetalleFacturaModal;
